import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { captureError, trackEvent } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type { Book } from '@/types/book'
import type {
  CreateRouletteData,
  Roulette,
  RouletteSpin,
  RouletteWithStats,
  UpdateRouletteData,
} from '@/types/roulette'

// Get all user's roulettes with stats
export function useRoulettes() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['roulettes', user?.id],
    queryFn: async (): Promise<RouletteWithStats[]> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('roulettes')
        .select(`
          *,
          roulette_books (
            id,
            book_id,
            books (
              id,
              title,
              author,
              status,
              cover_url,
              book_genres (
                genre:genres (
                  id,
                  name,
                  color
                )
              )
            )
          ),
          roulette_spins (
            id,
            selected_book_id,
            spun_at
          ),
          roulette_reading_status (
            current_book_id,
            books (
              id,
              title,
              author,
              cover_url
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('updated_at', { ascending: false })

      if (error) {
        captureError(error, { context: 'useRoulettes.queryFn', userId: user.id })
        throw error
      }

      // Transform data to include computed stats
      return data.map((roulette) => ({
        ...roulette,
        books:
          roulette.roulette_books?.map((rb: { books: Book }) => rb.books).filter(Boolean) || [],
        book_count: roulette.roulette_books?.length || 0,
        total_spins: roulette.roulette_spins?.length || 0,
        last_spin_at:
          roulette.roulette_spins?.sort(
            (a: RouletteSpin, b: RouletteSpin) =>
              new Date(b.spun_at).getTime() - new Date(a.spun_at).getTime()
          )?.[0]?.spun_at || null,
        current_reading: roulette.roulette_reading_status?.[0]?.books || null,
      }))
    },
    enabled: !!user?.id,
  })
}

// Get single roulette with full details
export function useRoulette(rouletteId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['roulette', rouletteId, user?.id],
    queryFn: async (): Promise<RouletteWithStats> => {
      if (!user?.id || !rouletteId) throw new Error('errors.userNotAuthenticated')

      const { data, error } = await supabase
        .from('roulettes')
        .select(`
          *,
          roulette_books (
            id,
            book_id,
            added_at,
            books (
              id,
              title,
              author,
              status,
              cover_url,
              rating,
              book_genres (
                genre:genres (
                  id,
                  name,
                  color
                )
              )
            )
          ),
          roulette_spins (
            id,
            selected_book_id,
            spin_duration,
            spun_at,
            books (
              id,
              title,
              author
            )
          ),
          roulette_reading_status (
            current_book_id,
            started_reading_at,
            books (
              id,
              title,
              author,
              cover_url
            )
          )
        `)
        .eq('id', rouletteId)
        .eq('user_id', user.id)
        .single()

      if (error) {
        captureError(error, { context: 'useRoulette.queryFn', rouletteId, userId: user.id })
        throw error
      }

      return {
        ...data,
        books: data.roulette_books?.map((rb: { books: Book }) => rb.books).filter(Boolean) || [],
        book_count: data.roulette_books?.length || 0,
        total_spins: data.roulette_spins?.length || 0,
        last_spin_at:
          data.roulette_spins?.sort(
            (a: RouletteSpin, b: RouletteSpin) =>
              new Date(b.spun_at).getTime() - new Date(a.spun_at).getTime()
          )?.[0]?.spun_at || null,
        current_reading: data.roulette_reading_status?.[0]?.books || null,
      }
    },
    enabled: !!user?.id && !!rouletteId,
  })
}

// Create new roulette
export function useCreateRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateRouletteData): Promise<Roulette> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      // Create roulette
      const { data: roulette, error: rouletteError } = await supabase
        .from('roulettes')
        .insert({
          user_id: user.id,
          name: data.name,
          description: data.description,
          color: data.color || '#8B5CF6',
          icon: data.icon || '🎲',
        })
        .select()
        .single()

      if (rouletteError) {
        captureError(rouletteError, {
          context: 'useCreateRoulette.createRoulette',
          userId: user.id,
        })
        throw rouletteError
      }

      // Add books to roulette
      if (data.book_ids.length > 0) {
        const { error: booksError } = await supabase.from('roulette_books').insert(
          data.book_ids.map((bookId) => ({
            roulette_id: roulette.id,
            book_id: bookId,
          }))
        )

        if (booksError) {
          captureError(booksError, {
            context: 'useCreateRoulette.addBooks',
            userId: user.id,
            rouletteId: roulette.id,
          })
          throw booksError
        }
      }

      trackEvent('roulette_created', {
        book_count: data.book_ids.length,
      })

      return roulette
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
    },
  })
}

// Update roulette
export function useUpdateRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      rouletteId,
      data,
    }: {
      rouletteId: string
      data: UpdateRouletteData
    }): Promise<Roulette> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { data: roulette, error } = await supabase
        .from('roulettes')
        .update(data)
        .eq('id', rouletteId)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) {
        captureError(error, { context: 'useUpdateRoulette', rouletteId, userId: user.id })
        throw error
      }
      return roulette
    },
    onSuccess: (_, { rouletteId }) => {
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['roulette', rouletteId, user?.id] })
    },
  })
}

// Delete roulette
export function useDeleteRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (rouletteId: string): Promise<void> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } = await supabase
        .from('roulettes')
        .delete()
        .eq('id', rouletteId)
        .eq('user_id', user.id)

      if (error) {
        captureError(error, { context: 'useDeleteRoulette', rouletteId, userId: user.id })
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
    },
  })
}

// Add book to roulette
export function useAddBookToRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      rouletteId,
      bookId,
    }: {
      rouletteId: string
      bookId: string
    }): Promise<void> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } = await supabase.from('roulette_books').insert({
        roulette_id: rouletteId,
        book_id: bookId,
      })

      if (error) {
        captureError(error, {
          context: 'useAddBookToRoulette',
          rouletteId,
          bookId,
          userId: user.id,
        })
        throw error
      }
    },
    onSuccess: (_, { rouletteId }) => {
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['roulette', rouletteId, user?.id] })
    },
  })
}

// Remove book from roulette
export function useRemoveBookFromRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      rouletteId,
      bookId,
    }: {
      rouletteId: string
      bookId: string
    }): Promise<void> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } = await supabase
        .from('roulette_books')
        .delete()
        .eq('roulette_id', rouletteId)
        .eq('book_id', bookId)

      if (error) {
        captureError(error, {
          context: 'useRemoveBookFromRoulette',
          rouletteId,
          bookId,
          userId: user.id,
        })
        throw error
      }
    },
    onSuccess: (_, { rouletteId }) => {
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['roulette', rouletteId, user?.id] })
    },
  })
}

// Record roulette spin
export function useSpinRoulette() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      rouletteId,
      selectedBookId,
      duration = 4000,
    }: {
      rouletteId: string
      selectedBookId: string
      duration?: number
    }): Promise<void> => {
      if (!user?.id) throw new Error('errors.userNotAuthenticated')

      const { error } = await supabase.from('roulette_spins').insert({
        roulette_id: rouletteId,
        selected_book_id: selectedBookId,
        spin_duration: duration,
      })

      if (error) {
        captureError(error, {
          context: 'useSpinRoulette',
          userId: user.id,
          rouletteId,
          selectedBookId,
        })
        throw error
      }
    },
    onSuccess: (_, { rouletteId, selectedBookId }) => {
      trackEvent('roulette_spin', {
        book_count: 0,
        result_book_id: selectedBookId,
      })
      // Force refetch all related queries to update UI immediately
      queryClient.invalidateQueries({ queryKey: ['roulettes', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['roulette', rouletteId, user?.id] })
      queryClient.invalidateQueries({ queryKey: ['roulette-history', rouletteId, user?.id] })
      // Also refetch data immediately for real-time updates
      queryClient.refetchQueries({ queryKey: ['roulettes', user?.id] })
      queryClient.refetchQueries({ queryKey: ['roulette', rouletteId, user?.id] })
      queryClient.refetchQueries({ queryKey: ['roulette-history', rouletteId, user?.id] })
    },
  })
}
